import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Instagram, MessageCircle } from "lucide-react";
import EnquiryForm from "@/components/landing/EnquiryForm";
import { useLang } from "@/i18n";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function Contact() {
  const { t } = useLang();

  return (
    <section id="contact" className="py-28 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div {...reveal} className="max-w-2xl">
          <p className="overline-label" data-testid="contact-overline">{t("cta_over")}</p>
          <h2 className="mt-6 font-serif font-semibold text-4xl sm:text-5xl tracking-tight leading-tight" data-testid="contact-heading">
            {t("cta_h1a")} <span className="text-brand-300">{t("cta_h1b")}</span>
          </h2>
          <p className="mt-6 text-base text-cream/55 leading-relaxed" data-testid="contact-subcopy">
            {t("cta_sub")}
          </p>
          <p className="mt-5 inline-block border border-brand/30 bg-brand/10 px-4 py-2.5 text-sm text-brand-200 leading-relaxed" data-testid="contact-quebec-note">
            {t("qc_note")}
          </p>
        </motion.div>

        <div className="mt-16 grid lg:grid-cols-12 gap-10">
          <motion.div {...reveal} className="lg:col-span-7">
            <EnquiryForm />
          </motion.div>

          <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.15 }} className="lg:col-span-5">
            <div className="h-full bg-navy-800/60 border border-brand/15 p-10 lg:p-12 flex flex-col" data-testid="contact-info-panel">
              <h3 className="font-serif font-semibold text-2xl text-cream">{t("info_h")}</h3>

              <div className="mt-10 space-y-8 flex-1">
                <div className="flex gap-5">
                  <Phone size={18} className="text-brand mt-1 flex-none" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-cream/40">{t("i_phone")}</p>
                    <a href="tel:+14039785035" data-testid="contact-info-phone" className="mt-1 block text-cream hover:text-brand transition-colors">
                      +1 403 978 5035
                    </a>
                  </div>
                </div>
                <div className="flex gap-5">
                  <Mail size={18} className="text-brand mt-1 flex-none" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-cream/40">{t("i_email")}</p>
                    <a href="mailto:earlyappointmentsinc@gmail.com" data-testid="contact-info-email" className="mt-1 block text-cream hover:text-brand transition-colors break-all">
                      earlyappointmentsinc@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex gap-5">
                  <MapPin size={18} className="text-brand mt-1 flex-none" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-cream/40">{t("i_loc")}</p>
                    <p className="mt-1 text-cream" data-testid="contact-info-address">
                      505 Main Street SW, Suite 1020,<br />Airdrie, Alberta,<br />T4B 3K3, Canada
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-brand/15">
                <p className="text-[10px] uppercase tracking-[0.25em] text-cream/40 mb-5">{t("i_follow")}</p>
                <div className="flex gap-4">
                  {[
                    { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/profile.php?id=61591786393398", testid: "social-facebook" },
                    { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/early_appointments_solutions/", testid: "social-instagram" },
                    { icon: MessageCircle, label: "WhatsApp", href: "https://wa.me/14039785035", testid: "social-whatsapp" },
                    { icon: Mail, label: "Email", href: "mailto:earlyappointmentsinc@gmail.com", testid: "social-email" },
                  ].map(({ icon: Icon, label, href, testid }) => (
                    <a
                      key={label}
                      href={href}
                      target={href.startsWith("http") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      aria-label={label}
                      data-testid={testid}
                      className="inline-flex items-center justify-center w-11 h-11 border border-brand/30 text-brand hover:bg-brand hover:text-white transition-colors duration-300"
                    >
                      <Icon size={18} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
